import { useEffect, useRef, useState } from "react";

import "./FramePhotoEditModal.css";

const getMinScale = (image, width, height) => {
  return Math.max(width / image.width, height / image.height);
};

const clampScale = (value, minScale) => {
  return Math.min(5, Math.max(minScale, value));
};

const clampOffset = (offset, image, scale, width, height) => {
  const minX = width - image.width * scale;
  const minY = height - image.height * scale;

  return {
    x: Math.min(0, Math.max(minX, offset.x)),
    y: Math.min(0, Math.max(minY, offset.y)),
  };
};

export default function CollagePhotoEditModal({
  image,
  width,
  height,
  grid,
  initialScale,
  initialOffset,
  onClose,
  onConfirm,
  onChangeImage,
}) {
  const canvasRef = useRef(null);
  const isDraggingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const pinchDistanceRef = useRef(null);
  const pinchStartScaleRef = useRef(initialScale);

  const [scale, setScale] = useState(initialScale);
  const [offset, setOffset] = useState(initialOffset);

  const minScale = getMinScale(image, width, height);

  useEffect(() => {
    setScale(initialScale);
    setOffset(initialOffset);
  }, [image, initialOffset, initialScale]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const limitedOffset = clampOffset(
      offset,
      image,
      scale,
      width,
      height,
    );

    canvas.width = width;
    canvas.height = height;

    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(
      image,
      limitedOffset.x,
      limitedOffset.y,
      image.width * scale,
      image.height * scale,
    );

    const cellWidth = width / grid.cols;
    const cellHeight = height / grid.rows;

    ctx.strokeStyle = "rgba(17, 17, 17, 0.45)";
    ctx.lineWidth = 2;

    for (let column = 0; column <= grid.cols; column += 1) {
      ctx.beginPath();
      ctx.moveTo(column * cellWidth, 0);
      ctx.lineTo(column * cellWidth, height);
      ctx.stroke();
    }

    for (let row = 0; row <= grid.rows; row += 1) {
      ctx.beginPath();
      ctx.moveTo(0, row * cellHeight);
      ctx.lineTo(width, row * cellHeight);
      ctx.stroke();
    }
  }, [grid.cols, grid.rows, height, image, offset, scale, width]);

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;

    return Math.sqrt(dx * dx + dy * dy);
  };

  const startMove = (event) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    if (event.touches?.length === 2) {
      pinchDistanceRef.current = getTouchDistance(event.touches);
      pinchStartScaleRef.current = scale;
      isDraggingRef.current = false;
      return;
    }

    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;

    isDraggingRef.current = true;
    lastPointRef.current = { x: clientX, y: clientY };
  };

  const movePhoto = (event) => {
    if (event.cancelable) {
      event.preventDefault();
    }

    if (event.touches?.length === 2 && pinchDistanceRef.current) {
      const distance = getTouchDistance(event.touches);
      const nextScale = clampScale(
        pinchStartScaleRef.current * (distance / pinchDistanceRef.current),
        minScale,
      );

      setScale(nextScale);
      setOffset((current) =>
        clampOffset(current, image, nextScale, width, height),
      );
      return;
    }

    if (!isDraggingRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = event.touches?.[0]?.clientX ?? event.clientX;
    const clientY = event.touches?.[0]?.clientY ?? event.clientY;
    const dx = (clientX - lastPointRef.current.x) * (width / rect.width);
    const dy = (clientY - lastPointRef.current.y) * (height / rect.height);

    setOffset((current) =>
      clampOffset(
        {
          x: current.x + dx,
          y: current.y + dy,
        },
        image,
        scale,
        width,
        height,
      ),
    );

    lastPointRef.current = { x: clientX, y: clientY };
  };

  const stopMove = () => {
    isDraggingRef.current = false;
    pinchDistanceRef.current = null;
  };

  const handleChangeImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    onChangeImage(file);
    event.target.value = "";
  };

  const handleConfirm = () => {
    onConfirm({
      scale,
      offset: clampOffset(offset, image, scale, width, height),
    });
  };

  return (
    <div className="frame-photo-modal" role="dialog" aria-modal="true">
      <div className="frame-photo-modal__panel">
        <div className="frame-photo-modal__header">
          <h2>Ajustar collage</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar">
            X
          </button>
        </div>

        <p className="frame-photo-modal__hint">
          Mueve la foto y amplia con dos dedos. Pulsa Listo para aplicar.
        </p>

        <canvas
          ref={canvasRef}
          aria-label="Editar encuadre del collage"
          onMouseDown={startMove}
          onMouseMove={movePhoto}
          onMouseUp={stopMove}
          onMouseLeave={stopMove}
          onTouchStart={startMove}
          onTouchMove={movePhoto}
          onTouchEnd={stopMove}
          onTouchCancel={stopMove}
        />

        <div className="frame-photo-modal__actions">
          <label className="frame-photo-modal__change">
            Cambiar foto
            <input
              type="file"
              accept="image/*"
              onChange={handleChangeImage}
            />
          </label>

          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" onClick={handleConfirm}>
            Listo
          </button>
        </div>
      </div>
    </div>
  );
}
